/* eslint-disable react/jsx-handler-names */
import classNames from "classnames";
import { useMemo, useRef, useState } from "react";
import {
  RegisterOptions,
  useController,
  useFormContext,
} from "react-hook-form";
import { animated, useSpring } from "@react-spring/web";
import useMeasure from "react-use-measure";

// Components
import InputSearch from "components/atoms/InputSearch";

// Hooks
import useTranslate from "hooks/useTranslate";
import useClickOutside from "hooks/useClickOutside";

// Icons
import ArrowIcon from "assets/icons/arrowDown.svg?react";
import CheckIcon from "assets/icons/banger/check.svg?react";

import "./styles.sass";

export type SelectOptionProps = {
  id: number | string;
  name: string;
  code?: string;
  icon?: JSX.Element;
  image?: string;
};

export type SelectProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  disabled?: boolean;
  name: string;
  label?: string;
  mode?: "light" | "dark";
  placeholder?: string;
  isSearchable?: boolean;
  options: SelectOptionProps[];
  defaultValue?: number | string;
  validation?: RegisterOptions;
  value: number | string | null;
  small?: boolean;
  noCheck?: boolean;
  returnName?: boolean;
  onChange: (value: number | string, props: SelectProps) => void;
};
const Select = (props: SelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  const t = useTranslate();

  const { control, watch } = useFormContext();

  const {
    defaultValue,
    label,
    name,
    validation,
    placeholder,
    // eslint-disable-next-line unused-imports/no-unused-vars
    mode = "light",
    isSearchable = false,
    options = [],
    value,
    small = false,
    noCheck = false,
    returnName = false,
    onChange,
  } = props;

  const [optionsRef, { height }] = useMeasure();
  const effect = useSpring({ height: isOpen ? height : 0 });
  const {
    field: fieldController,
    fieldState: { error },
  } = useController({ control, name, rules: validation, defaultValue });

  const toggleDropdown = () => {
    setIsOpen((prevState) => !prevState);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const handleOptionClick = (id: number | string) => {
    const selectedOption = options.find((option) => option.id === id);
    const returnValue = returnName && selectedOption ? selectedOption.name : id;
    onChange(returnValue, props);
    fieldController.onChange(returnValue);
    setIsOpen(false);
  };

  useClickOutside({ refs: [selectRef], handler: closeDropdown });

  const currentOption = useMemo(() => {
    if (returnName) {
      return (
        options.find((option) => option.name === value) ||
        (defaultValue && options.find((option) => option.id === defaultValue))
      );
    }
    return (
      options.find((option) => option.id === value) ||
      (defaultValue && options.find((option) => option.id === defaultValue))
    );
  }, [options, value, returnName, defaultValue]);

  const currentValue = useMemo(() => {
    if (value === null || value === undefined) {
      return placeholder;
    }
    if (currentOption) {
      if (currentOption.icon) {
        return `${currentOption.icon} ${currentOption.name}`;
      }
      return currentOption.name;
    }
    return "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOption, placeholder]);

  const searchQuery = watch(`${name}_search`);

  const filteredAndSortedOptions = useMemo(() => {
    const sortedOptions = [...options].sort((a, b) => {
      if (currentOption) {
        if (a.id === currentOption.id) {
          return -1;
        }
        if (b.id === currentOption.id) {
          return 1;
        }
      }
      return 0;
    });

    if (isSearchable && searchQuery) {
      return sortedOptions.filter((option) =>
        option.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    return sortedOptions;
  }, [options, currentOption, isSearchable, searchQuery]);

  const selectClassNames = classNames("Select");
  const inputClassNames = classNames("Select__input", {
    // eslint-disable-next-line camelcase
    Select__input_error: error,
    focused: isOpen,
  });
  const labelClassNames = classNames("Select__label", {
    withValue: currentValue,
    noLabel: !label,
  });
  const optionsClassNames = classNames("Select__options", {
    isSearchable,
  });
  const valueClassNames = classNames("Select__value", {
    withValue: currentValue,
  });
  const iconClassNames = classNames("Select__arrow", {
    opened: isOpen,
  });

  return (
    <div className={selectClassNames} ref={selectRef}>
      <div className={inputClassNames} onClick={toggleDropdown}>
        <div className="Select__border_left" />
        <div className={valueClassNames}>{currentValue}</div>
        <div className={labelClassNames}>
          <span>{label}</span>
        </div>
        <div className="Select__border_right" />
        <div className={iconClassNames}>
          <ArrowIcon />
        </div>
      </div>
      <animated.div className={optionsClassNames} style={effect}>
        <div ref={optionsRef}>
          {isSearchable && (
            <div className="Select__options_search">
              <InputSearch
                name={`${name}_search`}
                placeholder={t("placeholder.search")}
                value=""
              />
            </div>
          )}
          <div className="Select__options_list">
            {filteredAndSortedOptions.map((option) => {
              const optionClassNames = classNames("Select__options_option", {
                active: currentOption && option.id === currentOption.id,
                small: !!small,
              });
              return (
                <div
                  key={option.id}
                  className={optionClassNames}
                  onClick={() => handleOptionClick(option.id)}
                >
                  <div className="Select__options_option_name">
                    {option.icon && (
                      <div className="Select__options_option_icon">
                        {option.icon}
                      </div>
                    )}
                    {option.name}
                  </div>
                  {option.image && (
                    <div className="Select__options_option_image">
                      <img src={option.image} alt={option.name} />
                    </div>
                  )}
                  <div className="Select__options_option_check">
                    {!noCheck &&
                      currentOption &&
                      option.id === currentOption.id && <CheckIcon />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </animated.div>
    </div>
  );
};
export default Select;
